import { A4_ASPECT, LIBRARY_PREVIEW_LAYOUT_WIDTH_PX, LIBRARY_PREVIEW_SHEET_INSET_PX } from './libraryPreviewConstants';

/**
 * Fallback page height until the sheet has been measured. The sheet is laid out at
 * 960px, so this is a full A4 at that width. The 768px paper height is shorter than
 * the opened well's width-fit and leaves the real page clipped to the top crop.
 */
export const LIBRARY_PREVIEW_PAGE_HEIGHT_PX = LIBRARY_PREVIEW_LAYOUT_WIDTH_PX * A4_ASPECT;

export interface SheetPlacement {
  scale: number;
  xPx: number;
  yPx: number;
}

/**
 * Scale that fits the fixed-width layout sheet into the crop, anchored at the
 * sheet's top-left. An unmeasured crop (0, or not yet wider than the inset)
 * stays at 1 so the sheet is not collapsed before layout; the frame clips it
 * until a real width arrives.
 */
export function previewSheetScale(cropWidthPx: number): number {
  if (cropWidthPx <= LIBRARY_PREVIEW_SHEET_INSET_PX) return 1;

  const available = cropWidthPx - LIBRARY_PREVIEW_SHEET_INSET_PX;
  return available / LIBRARY_PREVIEW_LAYOUT_WIDTH_PX;
}

/** Resting crop: the 960px sheet pinned to the well's top-left inset. */
export function previewRestPlacement(cropWidthPx: number): SheetPlacement {
  return {
    scale: previewSheetScale(cropWidthPx),
    xPx: LIBRARY_PREVIEW_SHEET_INSET_PX,
    yPx: LIBRARY_PREVIEW_SHEET_INSET_PX
  };
}

/**
 * Hover/focus reveal: contain the whole page in the opened well and center it,
 * leaving the sheet inset so its shadow has canvas to fall on. An unmeasured
 * well stays on the resting crop.
 */
export function previewOpenPlacement(cropWidthPx: number, openHeightPx: number, sheetHeightPx: number): SheetPlacement {
  const rest = previewRestPlacement(cropWidthPx);
  if (cropWidthPx <= 0 || openHeightPx <= 0 || sheetHeightPx <= 0) return rest;

  const inset = LIBRARY_PREVIEW_SHEET_INSET_PX;
  const boxWidth = cropWidthPx - inset * 2;
  const boxHeight = openHeightPx - inset * 2;
  if (boxWidth <= 0 || boxHeight <= 0) return rest;

  const scale = Math.min(boxWidth / LIBRARY_PREVIEW_LAYOUT_WIDTH_PX, boxHeight / sheetHeightPx);
  return {
    scale,
    xPx: (cropWidthPx - LIBRARY_PREVIEW_LAYOUT_WIDTH_PX * scale) / 2,
    yPx: (openHeightPx - sheetHeightPx * scale) / 2
  };
}
